<script lang="ts">
  import { mdiClockOutline, mdiEmailOutline, mdiMapMarkerOutline, mdiPhoneOutline } from "@mdi/js"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import GhostButton from "../../../_shared/_modules/ghost-button"
  import type { OrganizationPresentation } from "../../../../api/public/presentation"
  import type { OrgPresentationMode } from "../../_shared/_logic/presentation-mode"
  import BusinessFAQSection from "./_modules/business-faq-section"
  import {
    buildBusinessFaqItems,
    formatAddressLines,
    formatMailHref,
    formatPhoneHref,
    formatServiceHoursLines,
  } from "./_logic/businessPresentation"

  const {
    organization,
    loading,
    error,
    load,
    presentationMode,
    hasCode = false,
  } = $props<{
    organization: OrganizationPresentation | null
    loading: boolean
    error: string | null
    load: () => Promise<void> | void
    presentationMode: OrgPresentationMode
    hasCode?: boolean
  }>()

  const phoneHref = $derived(formatPhoneHref(organization?.contact_phone))
  const mailHref = $derived(formatMailHref(organization?.contact_email))
  const addressLines = $derived(formatAddressLines(organization?.address))
  const serviceHourLines = $derived(formatServiceHoursLines(organization?.service_hours))
  const faqItems = $derived(buildBusinessFaqItems({ organization, presentationMode, hasCode }))
</script>

{#if loading}
  <div class="state-block">
    <p>Betriebsdaten werden geladen…</p>
  </div>
{:else if error}
  <div class="state-block">
    <p>{error}</p>
    <GhostButton onclick={load}>Erneut laden</GhostButton>
  </div>
{:else}
  <section class="business-page" aria-label="Betrieb">
    <div class="business-info">
      <SectionTitle
        title="Betriebsinformationen"
        brightBackground={true}
        styleOuter="margin-bottom: 0;"
      />

      <div class="contact-info-grid">
        {#if organization?.contact_phone && phoneHref}
          <section class="contact-info-block" aria-label="Telefon">
            <h3 class="contact-info-title">
              <span class="contact-info-title-icon" aria-hidden="true">
                <Icon path={mdiPhoneOutline} size="1rem" color="currentColor" />
              </span>
              <span>Telefon</span>
            </h3>
            <a class="contact-info-value" href={phoneHref}>{organization.contact_phone}</a>
          </section>
        {/if}
        {#if organization?.contact_email && mailHref}
          <section class="contact-info-block" aria-label="E-Mail">
            <h3 class="contact-info-title">
              <span class="contact-info-title-icon" aria-hidden="true">
                <Icon path={mdiEmailOutline} size="1rem" color="currentColor" />
              </span>
              <span>E-Mail</span>
            </h3>
            <a class="contact-info-value" href={mailHref}>{organization.contact_email}</a>
          </section>
        {/if}
        {#if (!organization?.contact_phone || !phoneHref) && (!organization?.contact_email || !mailHref)}
          <p class="muted">Keine öffentlichen Kontaktdaten hinterlegt.</p>
        {/if}

        <section class="contact-info-block" aria-label="Standort">
          <h3 class="contact-info-title">
            <span class="contact-info-title-icon" aria-hidden="true">
              <Icon path={mdiMapMarkerOutline} size="1rem" color="currentColor" />
            </span>
            <span>Standort</span>
          </h3>
          {#if addressLines.length}
            <div class="contact-info-value-group">
              {#each addressLines as line}
                <p>{line}</p>
              {/each}
            </div>
          {:else}
            <p class="muted">Keine öffentliche Adresse hinterlegt.</p>
          {/if}
        </section>

        <section class="contact-info-block contact-info-block-wide" aria-label="Öffnungszeiten">
          <h3 class="contact-info-title">
            <span class="contact-info-title-icon" aria-hidden="true">
              <Icon path={mdiClockOutline} size="1rem" color="currentColor" />
            </span>
            <span>Öffnungszeiten</span>
          </h3>
          <div class="contact-info-value-group">
            {#each serviceHourLines as line}
              <p>{line}</p>
            {/each}
          </div>
        </section>
      </div>
    </div>

    <BusinessFAQSection
      title="Hilfe zur Terminseite"
      items={faqItems}
      brightBackground={true}
      defaultOpenCount={2}
      mobileDefaultOpenCount={1}
    />
  </section>
{/if}

<style lang="less">
  .state-block {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;

    p {
      margin: 0;
    }
  }

  .business-page,
  .business-info {
    display: flex;
    flex-direction: column;
  }

  .business-page {
    gap: clamp(2rem, 4vw, 3rem);
  }

  .business-info {
    gap: 2rem;
  }

  .contact-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    gap: 1.6rem clamp(2rem, 5vw, 4rem);
    align-items: start;
  }

  h3,
  p {
    margin: 0;
  }

  .contact-info-block,
  .contact-info-value-group {
    display: flex;
    flex-direction: column;
  }

  .contact-info-block {
    min-width: 0;
    gap: 0.45rem;
    padding-left: 1rem;
    border-left: 2px solid var(--primary-100);
  }

  .contact-info-block-wide {
    grid-column: 1 / -1;
    max-width: 38rem;
  }

  .contact-info-title {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    color: var(--primary-100);
    font-size: 0.92rem;
    letter-spacing: 0;
    line-height: 1.15;
    text-transform: uppercase;
  }

  .contact-info-title-icon {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
  }

  .contact-info-value,
  .contact-info-value-group {
    width: fit-content;
    color: var(--text-invers-100);
    font-size: 1.05rem;
    font-weight: 400;
    line-height: 1.42;
  }

  .contact-info-value {
    text-decoration: none;

    &:hover {
      color: var(--primary-100);
    }
  }

  .contact-info-value-group {
    gap: 0.28rem;
  }

  .muted {
    color: color-mix(in srgb, var(--text-invers-100) 62%, transparent);
    font-size: 0.98rem;
  }

  @media (max-width: 760px) {
    .contact-info-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
